/* @flow */
import { Observable } from 'rxjs';
import { browserHistory } from 'react-router';
import { combineEpics } from 'redux-observable';
import { fromJS } from 'immutable';
import { prop, path } from 'ramda';
import type { ActionsObservable } from 'redux-observable';
import createDebugger from 'debug';
import { querystring } from 'trustar/utils';
const debug = createDebugger('app:modules:indicators:epics'); // eslint-disable-line no-unused-vars

import { computeDateRangeDaySelection, calculateDayRange } from 'trustar/utils/dashboard';
import { saveTextToFile } from 'trustar/utils/index';
import {
  IOC_ENRICHMENT_ENDPOINT,
  INDICATOR_LIST_ENDPOINT,
  INDICATOR_EXPORT_ENDPOINT,
} from './constants.js';
import {
  SET_TIME_RANGE,
  SET_TIME_RANGE_SUCCESS,
  DELETE_INDICATOR,
  DELETE_INDICATOR_FAILURE,
  FETCH_IOC_ENRICHMENT,
  FETCH_IOC_ENRICHMENT_SUCCESS,
  FETCH_IOC_ENRICHMENT_FAILURE,
  REVERT_CLASSIFICATION,
  REVERT_CLASSIFICATION_SUCCESS,
  REVERT_CLASSIFICATION_FAILURE,
  FETCH_INDICATOR_METADATA,
  FETCH_INDICATOR_METADATA_SUCCESS,
  FETCH_INDICATOR_METADATA_FAILURE,
  FETCH_INDICATORS_FOR_TAG,
  FETCH_INDICATORS_FOR_TAG_SUCCESS,
  FETCH_INDICATORS_FOR_TAG_FAILURE,
  EXPORT_INDICATORS_LIST,
  EXPORT_INDICATORS_LIST_SUCCESS,
  EXPORT_INDICATORS_LIST_FAILURE,
  EXPORT_INDICATORS_FOR_TAG,
  EXPORT_INDICATORS_FOR_TAG_SUCCESS,
  EXPORT_INDICATORS_FOR_TAG_FAILURE,
} from './action-types.js';
import { setFetchOptions, fetchIndicatorList } from './actions';
import { currentTimeFilter } from 'modules/insights/getters';
import { INDICATOR_TAGS_ENDPOINT } from 'modules/tags/types';
import { info, error, success } from 'modules/alerts/actions.js';
import {SEARCH_ENTITIES_SUCCESS} from 'modules/search/action-types';
import * as Search from 'modules/search';

import { normalizeIndicatorShape } from './utils';
import {formatSearchRequest} from '../search/epics';

const enrichmentUrl = value => querystring({ value }, IOC_ENRICHMENT_ENDPOINT);

const handleTimeRangeEpic = (action$, store, { ajax }) =>
  action$.ofType(SET_TIME_RANGE)
    .mergeMap(() => {

      const dateOptions = currentTimeFilter(store.getState());
      const daysBack = dateOptions.get('daysBack');

      const range = daysBack
        ? computeDateRangeDaySelection(daysBack)
        : {
          from: dateOptions.get('dateSince'),
          to: dateOptions.get('dateUntil'),
        };

      return Observable.from([
        setFetchOptions(range),
        { type: SET_TIME_RANGE_SUCCESS, payload: calculateDayRange(range.from, range.to) },
        fetchIndicatorList(),
      ]);

    });

/* Fetches metadata for Indicators[] */
const fetchIndicatorMetadataEpic = (action$, store, { ajax }) =>
  Observable.merge(
    action$.ofType(FETCH_INDICATOR_METADATA).map(prop('payload')),
    action$.ofType(SEARCH_ENTITIES_SUCCESS)
      .map(prop('payload'))
      .map(d => d.get('items'))
    )
      .mergeMap(indicators => {
        return Observable.from(indicators)
          .bufferCount(25)
            .mergeMap(batch => {
              return ajax.postJSON(`${INDICATOR_TAGS_ENDPOINT}/metadata`, batch)
                .map(prop('response'));
            })
            .map(metadata => metadata.map(normalizeIndicatorShape))
            .map(metadata => {
              return {
                type: FETCH_INDICATOR_METADATA_SUCCESS,
                payload: metadata
              };
            });
      })
  .catch(err => {
    return Observable.of({
      type: FETCH_INDICATOR_METADATA_FAILURE,
      payload: err,
      error: true,
      meta: {
        alert: { type: 'error', text: 'There was an error fetching indicator metadata.' },
      },
    });
  });

const fetchIndicatorsForTagEpic = (action$: ActionsObservable<*>, store: Store, { ajax }: Object) =>
  action$.ofType(FETCH_INDICATORS_FOR_TAG)
    .map(prop('payload'))
    .mergeMap(guid => {
      return ajax.get(`${INDICATOR_TAGS_ENDPOINT}/${guid}`)
        .map(prop('response'))
        .mergeMap(indicators => {
          return Observable.of({
            type: FETCH_INDICATORS_FOR_TAG_SUCCESS,
            payload: fromJS(indicators)
          });
        })
        .catch(err => {
          return Observable.from([
            { type: FETCH_INDICATORS_FOR_TAG_FAILURE },
            error('There was an error fetching summary data for this indicator tag.'),
          ]);
        });
    });

const fetchEnrichmentEpic = (action$: ActionsObservable<*>, store: Store, { ajax }: Object) =>
  action$.ofType(FETCH_IOC_ENRICHMENT)
    .map(prop('payload'))
    .switchMap(indicator => {
      return ajax.get(enrichmentUrl(indicator.value))
        .mergeMap(({ response }) => {
          return [{
            type: FETCH_IOC_ENRICHMENT_SUCCESS,
            payload: {
              id: normalizeIndicatorShape(indicator).id,
              enrichment: fromJS(response)
            }
          }];
        })
        .catch(err => {
          return [
            { type: FETCH_IOC_ENRICHMENT_FAILURE },
            error('There was an error fetching the indicator enrichment.')
          ];
        });
    });

const revertClassificationEpic = (action$: ActionsObservable<*>, store: Store, { ajax }: Object) =>
  action$.ofType(REVERT_CLASSIFICATION)
    .map(prop('payload'))
    .mergeMap(({ reportId, ioc }) => {
      return ajax.postJSON(`${IOC_ENRICHMENT_ENDPOINT}/revert/${reportId}`, ioc)
        .mergeMap(({ response }) => {
          return [{
            type: REVERT_CLASSIFICATION_SUCCESS,
            payload: { reportId, value: ioc.get('value') }
          }];
        })
        .catch(err => {
          return [
            { type: REVERT_CLASSIFICATION_FAILURE },
            error('There was an error reverting the indicator classification.')
          ];
        });
    });

const deleteIndicatorEpic = (action$: ActionsObservable<*>, store: Store, { ajax }: Object) =>
  action$.ofType(DELETE_INDICATOR)
    .map(prop('payload'))
    .mergeMap(payload => {
      return ajax.delete(querystring(payload, INDICATOR_LIST_ENDPOINT))
        .mergeMap(() => {
          return Observable.from([
            fetchIndicatorList(),
            success(`Indicator ${payload.type}|${payload.value} removed.`),
          ]);
        })
        .do(() => browserHistory.push('/browse/iocs'))
        .catch(err => {
          const message = path(['xhr', 'response', 'message'])(err);
          return Observable.from([
            { type: DELETE_INDICATOR_FAILURE, payload: err },
            error(message ? message : 'There was an error deleting the indicator.'),
          ]);
        });
    });

const exportEntitiesEpic = (action$: ActionsObservable<*>, store: Store, { ajax }: Object) =>
  action$.ofType(EXPORT_INDICATORS_LIST)
    .do(() => store.dispatch(info('Starting export, this could take several seconds.')))
    .mergeMap(request => {
      const state = store.getState();
      /* Grab current filters, and find what the total number of results is so we can set the pageSize to that */
      const entityQueryPayload = Search.getters.entityPageQuery(state);
      const totalResults = Search.getters.queryResults(state).getIn(['entities', 'totalElements']);

      const searchPayload = formatSearchRequest(entityQueryPayload)
        .set('type', 'INDICATOR')
        .set('pageSize', totalResults);

      return ajax({
        url: INDICATOR_EXPORT_ENDPOINT,
        method: 'POST',
        body: searchPayload,
        responseType: 'text', // <-- IMPORTANT. response will be null if the observable doesn't know its text
        headers: { Accept: '*/*', 'Content-Type': 'application/json; charset=UTF-8', },
      })
      .do(({ response }) => {
        saveTextToFile('indicators.csv', response);
      })
      .mergeMap(() => Observable.from([
        success('Export indicators success.'),
        { type: EXPORT_INDICATORS_LIST_SUCCESS }
      ]))
      .catch(err => {
        let errMessage = 'Could not export indicators';

        try {
          // AjaxObservable wraps the message field in the error response pretty deep and keeps it in string form, so we must try and parse
          const errResponseMessage = JSON.parse(err.response);
          errMessage = errResponseMessage.message || errMessage;
        }
        catch(error) {
          console.error(error);
        }

        return Observable.from([
          error(errMessage),
          { type: EXPORT_INDICATORS_LIST_FAILURE },
        ]);
      });
    });

const exportIndicatorsForTagEpic = (action$: ActionsObservable<*>, store: Store, { ajax }: Object) =>
  action$.ofType(EXPORT_INDICATORS_FOR_TAG)
    .map(prop('payload'))
    .do(() => store.dispatch(info('Starting export, this could take several seconds.')))
    .mergeMap(guid => {
      return ajax({
        url: `${INDICATOR_TAGS_ENDPOINT}/export?guid=${guid}`,
        method: 'GET',
        responseType: 'text', // <-- IMPORTANT. response will be null if the observable doesn't know its text
        headers: { Accept: '*/*', 'Content-Type': 'application/json; charset=UTF-8', },
      })
      .do(({ response }) => saveTextToFile('indicators.csv', response))
      .mergeMap(() => Observable.from([
        success('Export indicators success.'),
        { type: EXPORT_INDICATORS_FOR_TAG_SUCCESS }
      ]))
      .catch(err => Observable.from([
        error('Could not export indicators.'),
        { type: EXPORT_INDICATORS_FOR_TAG_FAILURE },
      ]));
    });


export default combineEpics(
  handleTimeRangeEpic,
  fetchIndicatorMetadataEpic,
  fetchIndicatorsForTagEpic,
  fetchEnrichmentEpic,
  revertClassificationEpic,
  deleteIndicatorEpic,
  exportEntitiesEpic,
  exportIndicatorsForTagEpic,
);



// WEBPACK FOOTER //
// ./src/app/modules/indicators/epics.js